import React, { useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  TooltipItem
} from "chart.js";

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

interface paramsTypes {
  chartData: any;
  selectedYear: string;
  selectedCity: any;
  isLoading: boolean;
  id: string;
}

const AnnualEvictionTrends = ({
  chartData,
  selectedYear,
  selectedCity,
  isLoading,
  id
}: paramsTypes) => {
  useEffect(() => {
    return () => {
      Chart.getChart(id)?.destroy();
    };
  }, [id]);

  const colorScale = [
    "#D6D6D6",
    "#BFBFBF",
    "#A9A9A9",
    "#949494",
    "#7F7F7F",
    "#4B4B4B"
  ];

  const styledChartData = chartData
    ? {
      ...chartData,
      datasets: chartData.datasets.map((dataset: any) => {
        const values = dataset.data || [];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const step = (max - min) / colorScale.length;

        return {
          ...dataset,
          backgroundColor: values.map((v: number) => {
            const index = Math.floor((v - min) / step);
            return colorScale[Math.min(index, colorScale.length - 1)];
          }),
          borderColor: "#fff",
          borderWidth: 1
        };
      })
    }
    : null;

  return (
    <div className="p-2 flex items-center justify-center" style={{ backgroundColor: "#f8f9fa" }}>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg
            className="w-5 h-5 animate-spin text-gray-700"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Loading chart data...
        </div>
      ) : styledChartData ? (
        <div style={{ minHeight: "250px", width: "100%", height: "100%" }}>
          <Bar
            id={id}
            data={styledChartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: false
                },
                title: {
                  display: true,
                  text: `Evictions - ${selectedYear}${selectedCity ? ` (${selectedCity.label})` : " (All Cities)"
                    }`,
                  font: {
                    size: 12,
                    weight: "bold"
                  },
                  padding: {
                    top: 0,
                    bottom: 10
                  },
                  align: "center"
                },
                tooltip: {
                  backgroundColor: "#f1f1f1",
                  borderColor: "#ccc",
                  borderWidth: 1,
                  bodyColor: "#333",
                  padding: 10,
                  callbacks: {
                    label: (context: TooltipItem<"bar">) => {
                      const value = context.parsed.y;
                      const dataset = context.chart.data.datasets[context.datasetIndex];
                      const dataArray = dataset.data as number[];
                      const total = dataArray.reduce((sum, val) => sum + val, 0);
                      const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
                      return `${context.label}: ${percentage}%`;
                    }
                  }
                }
              },
              scales: {
                x: {
                  type: "category",
                  labels: chartData?.labels ?? [],
                  grid: { display: false },
                  ticks: {
                    font: { size: 10 },
                    maxRotation: 0,
                    minRotation: 0,
                    callback: function (tickValue: string | number, index: number, ticks: any) {
                      const label = chartData?.labels?.[index] ?? String(tickValue);
                      return label.length > 7 ? label.slice(0, 7) + "…" : label;
                    }
                  }
                },
                y: {
                  grid: {
                    display: false,
                    color: "rgba(180, 180, 180, 0.2)"
                  },
                  ticks: {
                    font: { size: 10 },
                    callback: function (tickValue: string | number, index: number, ticks: any) {
                      if (typeof tickValue !== "number") return tickValue;
                      if (tickValue >= 1_000_000)
                        return (tickValue / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
                      if (tickValue >= 1_000)
                        return (tickValue / 1_000).toFixed(0) + "K";
                      return tickValue.toString();
                    }
                  }
                }
              },
              elements: {
                bar: {
                  borderRadius: [4, 4, 0, 0]
                }
              }
            }}
          />
        </div>
      ) : (
        <p className="text-sm text-gray-500">No data available.</p>
      )}
    </div>
  );
};

export default AnnualEvictionTrends;
